import React from "react";

/**
 * Small inline SVG icon set. Replaces emoji used as UI iconography, which
 * render differently per OS. Every icon is 24x24, stroke-based, currentColor.
 */
export type IconName =
  | "lock" | "unlock" | "fire" | "star" | "coin" | "moonrock" | "hammer" | "logout" | "chevron-left"
  | "chevron-right" | "chevron-down" | "close" | "info" | "refresh" | "check" | "egg" | "book"
  | "puzzle" | "paw" | "bag" | "user" | "trophy" | "sparkle" | "gift" | "help" | "settings" | "plus" | "play" | "more" | "plane" | "briefcase" | "school" | "music" | "heart" | "home" | "smile" | "clock" | "arrow-left" | "signal"
  | "volume" | "mic" | "eye" | "snail" | "arrow-right" | "speaker" | "ghost" | "swords" | "merge" | "users" | "flag" | "mail" | "snowflake" | "target" | "flip" | "box" | "brush" | "eraser" | "undo" | "hand"
  | "share" | "link" | "download" | "copy" | "whatsapp" | "message";

const PATHS: Record<IconName, React.ReactNode> = {
  share: <><path d="M12 15V4" /><path d="M8 8l4-4 4 4" /><path d="M6 12H5a1 1 0 0 0-1 1v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6a1 1 0 0 0-1-1h-1" /></>,
  link: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></>,
  download: <><path d="M12 4v11" /><path d="M8 11l4 4 4-4" /><path d="M5 19h14" /></>,
  whatsapp: <><path d="M3.5 20.5l1.2-4.3A8.6 8.6 0 1 1 7.9 19.4z" /><path d="M9 8.3c-.3 2.9 3.4 6.6 6.4 6.4l.9-1.6-2.1-1-1 .9c-1-.4-2.1-1.5-2.6-2.6l.9-1-1-2.1z" /></>,
  message: <><path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-8l-4.5 3.5V17H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><path d="M8 10.5h.01M12 10.5h.01M16 10.5h.01" /></>,
  copy: <><rect x="8" y="8" width="12" height="12" rx="2.5" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></>,
  lock: <><rect x="5" y="11" width="14" height="10" rx="2.5" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>,
  unlock: <><rect x="5" y="11" width="14" height="10" rx="2.5" /><path d="M8 11V8a4 4 0 0 1 7.5-2" /></>,
  fire: <path d="M12 3c1 3 4 4.5 4 8.5a4 4 0 0 1-8 0c0-1.5.6-2.5 1.4-3.3C9.5 10 10 11.5 11 12c.5-2 0-4 1-9z" />,
  star: <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.9l-5.3 2.8 1.1-5.9-4.3-4.1 5.9-.8z" />,
  coin: <><circle cx="12" cy="12" r="8.5" /><path d="M9.5 9.5h4a1.75 1.75 0 0 1 0 3.5h-4m0 0h4.5a1.75 1.75 0 0 1 0 3.5H9.5M12 7.5v9" /></>,
  moonrock: <path d="M12 3l6 4v10l-6 4-6-4V7z M12 3v18 M6 7l12 10 M18 7L6 17" />,
  hammer: <><path d="M14 4l6 6-2 2-6-6z" /><path d="M12 6l-8 8 2 2 8-8" /><path d="M4 14l6 6" /></>,
  logout: <><path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" /><path d="M14 8l4 4-4 4" /><path d="M18 12H9" /></>,
  "chevron-left": <path d="M15 5l-7 7 7 7" />,
  "chevron-right": <path d="M9 5l7 7-7 7" />,
  "chevron-down": <path d="M5 9l7 7 7-7" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
  refresh: <><path d="M20 12a8 8 0 1 1-2.3-5.7" /><path d="M20 4v5h-5" /></>,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  egg: <path d="M12 3c3.5 0 7 5 7 10a7 7 0 0 1-14 0c0-5 3.5-10 7-10z" />,
  book: <><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5z" /><path d="M4 20.5V5.5M20 18v3H6.5" /></>,
  puzzle: <path d="M10 4h4v2.5a1.5 1.5 0 0 0 3 0V4h3v4h-2.5a1.5 1.5 0 0 0 0 3H20v4h-3v-2.5a1.5 1.5 0 0 0-3 0V15h-4v2.5a1.5 1.5 0 0 1-3 0V15H4v-4h2.5a1.5 1.5 0 0 0 0-3H4V4h3v2.5a1.5 1.5 0 0 0 3 0z" />,
  paw: <><circle cx="7" cy="9" r="2" /><circle cx="12" cy="6.5" r="2" /><circle cx="17" cy="9" r="2" /><path d="M12 11c3 0 5.5 2.5 5.5 5a3 3 0 0 1-3 3c-1 0-1.7-.5-2.5-.5s-1.5.5-2.5.5a3 3 0 0 1-3-3c0-2.5 2.5-5 5.5-5z" /></>,
  bag: <><path d="M6 8h12l1 12H5z" /><path d="M9 8V6a3 3 0 0 1 6 0v2" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0 1 16 0" /></>,
  trophy: <><path d="M8 4h8v5a4 4 0 0 1-8 0z" /><path d="M8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4" /><path d="M12 13v4M8 21h8M10 17h4" /></>,
  sparkle: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />,
  gift: <><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M4 10h16V7H4zM12 7v13M12 7c-2-3-5-3-5-1s3 1 5 1zm0 0c2-3 5-3 5-1s-3 1-5 1z" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.7M12 17h.01" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2-1.2L14.3 3h-4.6l-.3 2.7a7 7 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 2 1.2l.3 2.7h4.6l.3-2.7a7 7 0 0 0 2-1.2l2.3 1 2-3.4-2-1.5c.1-.4.1-.8.1-1.2z" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  play: <path d="M8 5l12 7-12 7z" />,
  more: <><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></>,
  plane: <path d="M10.5 13.5L4 11l1.5-1.5 6 1 5-5.5a1.8 1.8 0 0 1 2.5 2.5l-5.5 5 1 6L13 20l-2.5-6.5z" />,
  briefcase: <><rect x="3" y="8" width="18" height="12" rx="2" /><path d="M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18" /></>,
  school: <><path d="M2 9l10-5 10 5-10 5z" /><path d="M6 11.5V16c0 1.5 3 3 6 3s6-1.5 6-3v-4.5M22 9v6" /></>,
  music: <><path d="M9 18V6l10-2v12" /><circle cx="6.5" cy="18" r="2.5" /><circle cx="16.5" cy="16" r="2.5" /></>,
  heart: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />,
  home: <><path d="M4 11l8-7 8 7v9a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1z" /></>,
  smile: <><circle cx="12" cy="12" r="9" /><path d="M8.5 14.5a4.5 4.5 0 0 0 7 0M9 10h.01M15 10h.01" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
  "arrow-left": <path d="M19 12H5M11 6l-6 6 6 6" />,
  signal: <path d="M4 18h2v2H4zM8 14h2v6H8zM12 10h2v10h-2zM16 6h2v14h-2z" />,
  volume: <><path d="M4 9.5v5h3.5L12 18V6L7.5 9.5z" /><path d="M15 9.5a3.6 3.6 0 0 1 0 5" /><path d="M17.5 7a7 7 0 0 1 0 10" /></>,
  mic: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3M9 21h6" /></>,
  eye: <><path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" /><circle cx="12" cy="12" r="3" /></>,
  snail: <><circle cx="9" cy="13" r="4.5" /><path d="M9 13h.01" /><path d="M13.5 13.5c2.5 0 4-1.5 4.8-4.8" /><path d="M18.3 8.7l-1.5-2.2M18.3 8.7l2.4-1.2" /><path d="M3 19h17" /></>,
  "arrow-right": <path d="M5 12h14M13 6l6 6-6 6" />,
  speaker: <><path d="M4 9.5v5h3.5L13 19V5L7.5 9.5z" /><path d="M16 9a4 4 0 0 1 0 6" /><path d="M18.5 6.5a7.5 7.5 0 0 1 0 11" /></>,
  ghost: <><path d="M6 20V11a6 6 0 0 1 12 0v9l-2.4-1.8L13.2 20 12 18.5 10.8 20l-2.4-1.8z" /><path d="M9.5 11h.01M14.5 11h.01" /></>,
  swords: <><path d="M4 4l10.5 10.5" /><path d="M20 4L9.5 14.5" /><path d="M6.5 14.5l3 3" /><path d="M17.5 14.5l-3 3" /><path d="M5 19l2.5-2.5M19 19l-2.5-2.5" /></>,
  merge: <><rect x="3" y="8" width="8" height="8" rx="2.5" /><rect x="13" y="8" width="8" height="8" rx="2.5" /><path d="M11 12h2" /></>,
  users: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" /><path d="M18 14.2a6.5 6.5 0 0 1 3.5 5.8" /></>,
  flag: <><path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M4 7l8 6 8-6" /></>,
  snowflake: <><path d="M12 2.5v19M3.8 7.25l16.4 9.5M20.2 7.25L3.8 16.75" /><path d="M9.5 4L12 6.2 14.5 4M9.5 20L12 17.8 14.5 20M4.2 10.6L7.2 9.6 6.6 6.5M19.8 13.4l-3 1 .6 3.1M19.8 10.6l-3-1 .6-3.1M4.2 13.4l3 1-.6 3.1" /></>,
  target: <><circle cx="12" cy="12" r="3.5" /><path d="M12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4" /></>,
  flip: <><path d="M12 3v18" /><path d="M9 7L4 17h5z" /><path d="M15 7l5 10h-5z" /></>,
  box: <><path d="M4 8l8-4 8 4v8l-8 4-8-4z" /><path d="M4 8l8 4 8-4M12 12v8" /></>,
  brush: <><path d="M14.5 4.5l5 5-7.5 7.5-5-5z" /><path d="M7 12l-1.5 1.5a2.5 2.5 0 0 0 0 3.5c.7.7 1 1.7.5 3.5 2 .2 3.4-.3 4.5-1.5L12 17" /></>,
  eraser: <><path d="M16 4l5 5-9.5 9.5H7L3.5 15z" /><path d="M10.5 9.5l5 5M7 18.5h13" /></>,
  undo: <><path d="M9 7L4 12l5 5" /><path d="M4 12h10a6 6 0 0 1 0 12h-2" transform="translate(0 -6)" /></>,
  hand: <><path d="M8 12V6.5a1.5 1.5 0 0 1 3 0V11" /><path d="M11 10.5V5a1.5 1.5 0 0 1 3 0v6" /><path d="M14 10.5V6.5a1.5 1.5 0 0 1 3 0V14a6 6 0 0 1-6 6h-.5a5 5 0 0 1-4-2l-2.8-4a1.5 1.5 0 0 1 2.3-1.9L8 14" /></>,
};

export interface IconProps extends React.SVGAttributes<SVGSVGElement> {
  name: IconName;
  size?: number;
  /** Filled icons (star, fire, coin badge) read better solid. */
  filled?: boolean;
  title?: string;
}

// Currency icons use the game's real art (the same images as the HUD), never a
// flat SVG cut-out.
const S3_ART = "https://moonrock-images.s3.us-east-2.amazonaws.com/";
const RESOURCE_ART: Partial<Record<IconName, string>> = {
  star: S3_ART + "Star-Resource.png",
  moonrock: S3_ART + "moonrockIcon.png",
  coin: S3_ART + "GoldCoin9.png",
};

export default function Icon({ name, size = 20, filled = false, title, ...rest }: IconProps) {
  const art = RESOURCE_ART[name];
  if (art) {
    const { className, style } = rest as { className?: string; style?: React.CSSProperties };
    return (
      <img
        src={art}
        width={size}
        height={size}
        alt={title || ""}
        aria-hidden={title ? undefined : true}
        draggable={false}
        className={`mr-icon-art mr-icon-art--${name}${className ? ` ${className}` : ""}`}
        style={{ objectFit: "contain", display: "inline-block", verticalAlign: "middle", flex: "none", ...style }}
      />
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={filled ? 1.2 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
